/**
 * TestResultsBlock — renders a `test_results` content block as a compact table.
 *
 * Uses a light custom Ant Table renderer rather than BloodResultsTable or
 * TestResultsTab which both expect a full `Test` order object we don't have here.
 *
 * Flag colours:
 *   normal         → green  (success)
 *   low | high     → amber  (warning)
 *   critical_low | critical_high → red (error)
 *   unknown        → default (grey)
 */

import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { XoeTestResultRow } from '../../../types/xoe/XoeTypes';
import RangeSourceTag from '../../blood/RangeSourceTag';
import type { TestResultsBlockProps } from '../../../types/xoe/XoeUiTypes';

const { Text } = Typography;

function flagColor(flag: string | undefined): string {
	switch (flag) {
		case 'normal':        return 'success';
		case 'low':
		case 'high':          return 'warning';
		case 'critical_low':
		case 'critical_high': return 'error';
		default:              return 'default';
	}
}

function flagLabel(flag: string | undefined): string {
	if (!flag) return '—';
	return flag.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const COLUMNS: ColumnsType<XoeTestResultRow> = [
	{
		title:     <Text style={{ fontSize: 'var(--mg-fs-xxs)' }}>Test</Text>,
		dataIndex: 'name',
		key:       'name',
		render:    (v: string) => <Text style={{ fontSize: 'var(--mg-fs-xxs)' }}>{v}</Text>,
	},
	{
		title: <Text style={{ fontSize: 'var(--mg-fs-xxs)' }}>Value</Text>,
		key:   'value',
		render: (_: unknown, row: XoeTestResultRow) => (
			<Text style={{ fontSize: 'var(--mg-fs-xxs)' }}>
				{row.value}{row.unit ? ` ${row.unit}` : ''}
			</Text>
		),
	},
	{
		title:     <Text style={{ fontSize: 'var(--mg-fs-xxs)' }}>Reference range</Text>,
		dataIndex: 'reference_range',
		key:       'reference_range',
		render:    (v: string | undefined, row: XoeTestResultRow) => (
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--mg-sp-2)' }}>
				<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xxs)' }}>{v || '—'}</Text>
				<RangeSourceTag source={row.range_source} labFlag={null} />
			</span>
		),
	},
	{
		title: <Text style={{ fontSize: 'var(--mg-fs-xxs)' }}>Flag</Text>,
		key:   'flag',
		render: (_: unknown, row: XoeTestResultRow) => (
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--mg-sp-2)' }}>
				<Tag color={flagColor(row.flag)} style={{ fontSize: 'var(--mg-fs-xxs)', margin: 0 }}>
					{flagLabel(row.flag)}
				</Tag>
				<RangeSourceTag source={null} labFlag={row.lab_flag} />
			</span>
		),
	},
];

const TestResultsBlock: React.FC<TestResultsBlockProps> = ({ results }) => {
	if (!results || results.length === 0) {
		return (
			<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xxs)' }}>
				No test results to display.
			</Text>
		);
	}

	return (
		<Table<XoeTestResultRow>
			dataSource={results}
			columns={COLUMNS}
			rowKey={(row, idx) => `${row.name}-${idx ?? 0}`}
			size="small"
			pagination={false}
			style={{ marginTop: 'var(--mg-sp-4)' }}
		/>
	);
};

export default TestResultsBlock;
